import { useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { BLOCK_SERVICE_API_URL } from './services/snippetMediaClient';
import { SnippetBodyFormat } from './types';
import type { SnippetProduct, SnippetScope, SnippetData, SnippetsProvider } from './types';

const SNIPPETS_PATH = '/snippets';

export interface FetchSnippetParams {
	format: SnippetBodyFormat;
	product: SnippetProduct;
	scope: SnippetScope;
}

export type SnippetProviderParams = FetchSnippetParams;

export const fetchSnippets = async (params: FetchSnippetParams) => {
	const queryParams = new URLSearchParams({
		format: params.format,
		product: params.product,
		scope: params.scope,
	});

	const response = await fetch(
		`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${queryParams.toString()}`,
		{
			method: 'GET',
		},
	);

	if (!response.ok) {
		throw new Error('Failed to fetch snippets');
	}

	return await response.json();
};

export const createSnippetsProvider = (params: SnippetProviderParams): SnippetsProvider => ({
	getSnippets: async (): Promise<SnippetData[]> => {
		const responseData = await fetchSnippets(params);

		if (!responseData.success) {
			return [];
		}

		let snippets = responseData.success;
		if (!Array.isArray(snippets)) {
			snippets = [snippets];
		}

		return snippets
			.map((snippet: Omit<SnippetData, 'body'> & { body: string }) => {
				const snippetBody = parseSnippetContent(snippet.body);
				if (snippetBody === undefined) {
					return undefined;
				}

				return { ...snippet, body: snippetBody } as SnippetData;
			})
			.filter((snippet: SnippetData | undefined): snippet is SnippetData => snippet !== undefined);
	},
});

function filterValidAdfNodes(nodes: (ADFEntity | null | undefined)[]): ADFEntity[] {
	return nodes.filter((node): node is ADFEntity => node !== undefined && node !== null);
}

const parseSnippetContent = (content: string): ADFEntity | undefined => {
	try {
		const parsedContent = JSON.parse(content);

		// Handle fragment (array of nodes without doc wrapper)
		if (Array.isArray(parsedContent)) {
			return wrapAdfWithDocNode(filterValidAdfNodes(parsedContent));
		}

		const adfContent = parsedContent as ADFEntity;

		if (adfContent.type === 'doc' && Array.isArray(adfContent.content)) {
			return { ...adfContent, content: filterValidAdfNodes(adfContent.content) };
		}

		return wrapAdfWithDocNode(adfContent);
	} catch (error) {
		return undefined;
	}
};

const wrapAdfWithDocNode = (adf: ADFEntity | ADFEntity[]): ADFEntity => {
	const docType = 'doc';
	const isArray = Array.isArray(adf);
	if (!isArray && adf.type === docType) {
		return adf;
	}

	const content: ADFEntity[] = isArray ? adf : [adf];
	return {
		version: 1,
		type: docType,
		content,
	};
};

export interface SnippetsPluginOptionsParams {
	product: SnippetProduct;
	scope: SnippetScope;
}

export const useSnippetsPluginOptions = ({
	product,
	scope,
}: SnippetsPluginOptionsParams): { snippetsProvider: SnippetsProvider } =>
	useMemo(
		() => ({
			snippetsProvider: createSnippetsProvider({
				format: SnippetBodyFormat.ADF,
				product,
				scope,
			}),
		}),
		[product, scope],
	);
